import type { AdminArtifactView, AdminSortKey, Visibility } from "../../lib/api";
import { SORT_COLUMNS, type SortState } from "./sortState";

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function formatDate(s: string | null): string {
  if (!s) return "—";
  return new Date(s).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function relativeExpiry(expires_at: string): string {
  const ms = new Date(expires_at).getTime() - Date.now();
  const days = Math.round(ms / (1000 * 60 * 60 * 24));
  if (days > 1) return `in ${days}d`;
  if (days === 1) return "in 1d";
  if (days === 0) return "today";
  if (days === -1) return "1d ago";
  return `${Math.abs(days)}d ago`;
}

const VISIBILITY_CHIP: Record<Visibility, string> = {
  private: "bg-slate-100 text-slate-700",
  unlisted: "bg-blue-100 text-blue-800",
  shared: "bg-emerald-100 text-emerald-800",
};

/** Map an active sort + the keys a header hosts to an `aria-sort` value. */
function ariaSort(sort: SortState, keys: AdminSortKey[]): "ascending" | "descending" | "none" {
  if (!keys.includes(sort.key)) return "none";
  return sort.dir === "asc" ? "ascending" : "descending";
}

/** A clickable column label that toggles/sets the table sort. */
function SortButton({
  column,
  sort,
  onSort,
}: {
  column: AdminSortKey;
  sort: SortState;
  onSort: (key: AdminSortKey) => void;
}) {
  const active = sort.key === column;
  const { label } = SORT_COLUMNS[column];
  return (
    <button
      type="button"
      onClick={() => onSort(column)}
      className={`group inline-flex items-center gap-1 ${
        active ? "text-slate-900" : "text-slate-500 hover:text-slate-700"
      }`}
      title={`Sort by ${label}`}
    >
      <span>{label}</span>
      <span
        aria-hidden
        className={active ? "text-slate-900" : "text-slate-300 group-hover:text-slate-400"}
      >
        {active ? (sort.dir === "asc" ? "↑" : "↓") : "↕"}
      </span>
    </button>
  );
}

/** A single-key sortable `<th>` (label + indicator + `aria-sort`). */
function SortableTh({
  column,
  sort,
  onSort,
}: {
  column: AdminSortKey;
  sort: SortState;
  onSort: (key: AdminSortKey) => void;
}) {
  return (
    <th className="px-3 py-2" aria-sort={ariaSort(sort, [column])}>
      <SortButton column={column} sort={sort} onSort={onSort} />
    </th>
  );
}

interface Props {
  /** Artifacts for the current page, already ordered server-side. */
  items: AdminArtifactView[];
  /** Active sort (drives header indicators). */
  sort: SortState;
  /** Called with the clicked column's sort key. */
  onSort: (key: AdminSortKey) => void;
  /** Per-row in-flight action marker, e.g. ``vis:<slug>`` / ``del:<slug>``. */
  busy: string | null;
  /** Viewer's email — used to flag private artifacts the admin doesn't own. */
  currentUserEmail: string | undefined;
  onChangeVisibility: (slug: string, next: Visibility) => void;
  onTogglePermanent: (slug: string, current: boolean) => void;
  onRestore: (slug: string) => void;
  onForceDelete: (slug: string, title: string) => void;
}

/**
 * Governance table for the admin "All Artifacts" page. Presentational: the
 * parent owns data, sort state, and the action callbacks. Column headers are
 * clickable to sort (the combined Title/owner header exposes both keys).
 */
export function AllArtifactsTable({
  items,
  sort,
  onSort,
  busy,
  currentUserEmail,
  onChangeVisibility,
  onTogglePermanent,
  onRestore,
  onForceDelete,
}: Props) {
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
      <table className="w-full text-sm">
        <thead className="bg-slate-50">
          <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            <th className="px-3 py-2">Slug</th>
            <th className="px-3 py-2" aria-sort={ariaSort(sort, ["title", "owner"])}>
              <div className="flex flex-col items-start gap-0.5">
                <SortButton column="title" sort={sort} onSort={onSort} />
                <SortButton column="owner" sort={sort} onSort={onSort} />
              </div>
            </th>
            <SortableTh column="visibility" sort={sort} onSort={onSort} />
            <SortableTh column="size" sort={sort} onSort={onSort} />
            <SortableTh column="views" sort={sort} onSort={onSort} />
            <SortableTh column="last_viewed" sort={sort} onSort={onSort} />
            <SortableTh column="ttl" sort={sort} onSort={onSort} />
            <SortableTh column="updated" sort={sort} onSort={onSort} />
            <th className="px-3 py-2 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200">
          {items.map((a) => {
            const isDeleted = !!a.deleted_at;
            const visBusy = busy === `vis:${a.slug}`;
            const mine = a.owner_email === currentUserEmail;
            // Private is owner-only: admins can't open it (backend 404s) and
            // can't change its visibility (backend 403s — that would be a
            // read-bypass). Reflect both in the UI.
            const ownerOnly = a.visibility === "private" && !mine;
            return (
              <tr key={a.slug} className={isDeleted ? "bg-slate-50 text-slate-400" : ""}>
                <td className="px-3 py-2 font-mono text-xs">
                  {isDeleted ? <s>{a.slug}</s> : a.slug}
                </td>
                <td className="px-3 py-2">
                  <div className="font-medium">{a.title}</div>
                  <div className="text-xs text-slate-500">{a.owner_email}</div>
                </td>
                <td className="px-3 py-2">
                  <select
                    value={a.visibility}
                    onChange={(e) => onChangeVisibility(a.slug, e.target.value as Visibility)}
                    disabled={visBusy || isDeleted || a.visibility === "private"}
                    title={
                      a.visibility === "private"
                        ? "Private is owner-only — only the owner can change its visibility"
                        : undefined
                    }
                    className={`rounded px-2 py-0.5 text-xs ${VISIBILITY_CHIP[a.visibility]} disabled:opacity-50`}
                  >
                    <option value="private">Private</option>
                    <option value="unlisted">Unlisted</option>
                    <option value="shared">Shared</option>
                  </select>
                </td>
                <td className="px-3 py-2">{formatBytes(a.size_bytes)}</td>
                <td className="px-3 py-2">{a.view_count}</td>
                <td className="px-3 py-2 text-slate-600">{formatDate(a.last_viewed_at)}</td>
                <td className="px-3 py-2 text-slate-600">
                  {a.permanent ? (
                    <span className="rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-800">
                      Permanent
                    </span>
                  ) : (
                    <span title={formatDate(a.expires_at)}>{relativeExpiry(a.expires_at)}</span>
                  )}
                </td>
                <td className="px-3 py-2 text-slate-600">{formatDate(a.updated_at)}</td>
                <td className="px-3 py-2 text-right">
                  <div className="flex justify-end gap-2">
                    {!isDeleted ? (
                      <>
                        {ownerOnly ? (
                          <span
                            title="Private — only the owner can open this artifact"
                            aria-label="Private — only the owner can open this artifact"
                            className="rounded border border-slate-200 px-2 py-1 text-xs text-slate-400"
                          >
                            🔒 Owner-only
                          </span>
                        ) : (
                          <a
                            href={a.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="rounded border border-slate-200 px-2 py-1 text-xs hover:bg-slate-50"
                          >
                            View
                          </a>
                        )}
                        <button
                          type="button"
                          onClick={() => onTogglePermanent(a.slug, a.permanent)}
                          disabled={busy === `perm:${a.slug}`}
                          className="rounded border border-violet-200 px-2 py-1 text-xs text-violet-700 hover:bg-violet-50 disabled:opacity-50"
                          title={
                            a.permanent
                              ? "Allow this artifact to expire on its TTL again"
                              : "Pin this artifact so the daily purge sweep skips it"
                          }
                        >
                          {a.permanent ? "Make temporary" : "Make permanent"}
                        </button>
                        <button
                          type="button"
                          onClick={() => onForceDelete(a.slug, a.title)}
                          disabled={busy === `del:${a.slug}`}
                          className="rounded border border-rose-200 px-2 py-1 text-xs text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                        >
                          Force-delete
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => onRestore(a.slug)}
                          disabled={busy === `restore:${a.slug}`}
                          className="rounded border border-emerald-200 px-2 py-1 text-xs text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                        >
                          Restore
                        </button>
                        <button
                          type="button"
                          onClick={() => onForceDelete(a.slug, a.title)}
                          disabled={busy === `del:${a.slug}`}
                          className="rounded border border-rose-200 px-2 py-1 text-xs text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                        >
                          Purge now
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
